'use client'

import { PageContainer } from '@/components/page-container'
import { Button } from '@/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { brand } from '@/config/brand'
import { useSubscriptions } from '@/hooks/queries'
import { hasConfigAccess } from '@/services/payments.service'
import {
	ClientApp,
	ClientOs,
	SubLink,
	SubService,
} from '@/services/sub.service'
import {
	AlertTriangle,
	Copy,
	Download,
	ExternalLink,
	FileQuestion,
	Globe,
	Link2,
	QrCode,
	ShieldQuestion,
	Smartphone,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import QRCode from 'react-qr-code'
import { toast } from 'sonner'

const OS_OPTIONS: { key: ClientOs; label: string }[] = [
	{ key: 'ios', label: 'iOS' },
	{ key: 'android', label: 'Android' },
	{ key: 'windows', label: 'Windows' },
	{ key: 'macos', label: 'macOS' },
	{ key: 'linux', label: 'Linux' },
]

/** Определение ОS по userAgent (выбор можно переключить вручную) */
function detectOs(): ClientOs {
	if (typeof navigator === 'undefined') return 'windows'
	const ua = navigator.userAgent.toLowerCase()
	const touch = navigator.maxTouchPoints || 0
	if (/iphone|ipad|ipod/.test(ua)) return 'ios'
	// iPadOS 13+ маскируется под macOS — отличаем по тач-точкам
	if (/macintosh|mac os x/.test(ua) && touch > 1) return 'ios'
	if (/android/.test(ua)) return 'android'
	if (/windows/.test(ua)) return 'windows'
	if (/macintosh|mac os x/.test(ua)) return 'macos'
	if (/linux/.test(ua)) return 'linux'
	return 'windows'
}

/** Сборка deep-link из scheme_template: {url_enc} → encodeURIComponent, {url} → как есть */
function buildDeepLink(scheme: string, url: string): string {
	return scheme
		.replace(/\{url_enc\}/g, encodeURIComponent(url))
		.replace(/\{url\}/g, url)
}

export default function ConfigurationPage() {
	const router = useRouter()
	const [link, setLink] = useState<SubLink>()
	const [linkLoading, setLinkLoading] = useState(true)
	const [os, setOs] = useState<ClientOs>()
	const [apps, setApps] = useState<ClientApp[]>([])
	const [appsLoading, setAppsLoading] = useState(false)
	// Готовые deep-link'и по app_key для приложений с шифрованием (Happ crypt4). Ссылку строит
	// бэкенд; предзагружаем, чтобы клик открывал приложение синхронно (иначе iOS не откроет deep-link).
	const [deepLinks, setDeepLinks] = useState<Record<string, string>>({})

	// Гейт доступа: страница доступна только при активной подписке в статусе 1/3
	// (та же проверка, что прячет пункт в сайдбаре). Нет подписок, пустой массив
	// или неподходящий статус → редирект в личный кабинет. Ждём успешной загрузки,
	// чтобы не выкидывать на транзиентной ошибке/во время первого запроса.
	const { data: subsData, isSuccess: subsLoaded } = useSubscriptions()
	const allowed = hasConfigAccess(subsData)
	useEffect(() => {
		if (subsLoaded && !allowed) router.replace('/profile')
	}, [subsLoaded, allowed, router])

	// Ссылка-подписка + автоопределение ОС — один раз при монтировании
	useEffect(() => {
		setOs(detectOs())
		SubService.getMyLink()
			.then(setLink)
			.catch(error => {
				toast.error(
					error.response?.data?.message || 'Ошибка получения ссылки-подписки'
				)
			})
			.finally(() => setLinkLoading(false))
	}, [])

	// Каталог приложений — при каждой смене ОС
	useEffect(() => {
		if (!os) return
		setAppsLoading(true)
		setDeepLinks({})
		SubService.getApps(os)
			.then(list => {
				const sorted = Array.isArray(list)
					? [...list].sort((a, b) => a.sort - b.sort)
					: []
				setApps(sorted)

				// Предзагружаем зашифрованные ссылки (их строит бэкенд) — по одной на приложение.
				sorted
					.filter(app => app.link_encryption && app.link_encryption !== 'none')
					.forEach(app => {
						SubService.getMyLink(app.app_key)
							.then(res => {
								if (res.deep_link) {
									setDeepLinks(prev => ({ ...prev, [app.app_key]: res.deep_link! }))
								}
							})
							.catch(() => {})
					})
			})
			.catch(() => {
				setApps([])
				toast.error('Ошибка получения списка приложений')
			})
			.finally(() => setAppsLoading(false))
	}, [os])

	function copyLink() {
		if (!link?.url) return
		navigator.clipboard.writeText(link.url)
		toast.success('Ссылка скопирована в буфер обмена')
	}

	async function openInApp(app: ClientApp) {
		if (!link?.url) return
		const encrypted = !!app.link_encryption && app.link_encryption !== 'none'

		// Зашифрованные (Happ crypt4): ссылку строит бэкенд. Берём предзагруженную СИНХРОННО —
		// deep-link на iOS открывается только в жесте клика, await бы разорвал жест.
		if (encrypted) {
			const ready = deepLinks[app.app_key]
			if (ready) {
				window.location.href = ready
				return
			}
			// Фолбэк: не успели предзагрузить — тянем сейчас (редкий случай).
			try {
				const res = await SubService.getMyLink(app.app_key)
				if (res.deep_link) {
					window.location.href = res.deep_link
					return
				}
			} catch {}
		}

		if (app.scheme_template) {
			window.location.href = buildDeepLink(app.scheme_template, link.url)
		}
	}

	// Подписки ещё грузятся — показываем скелетон вместо пустого экрана.
	if (!subsLoaded) return <ConfigurationSkeleton />
	// Доступа нет — вот-вот сработает редирект в личный кабинет, контент не нужен.
	if (!allowed) return null

	return (
		<div className='min-h-screen bg-gray-950'>
			<PageContainer className='py-6 lg:py-10'>
					<div className='space-y-4 lg:space-y-6'>
						<div className='animate-in fade-in-0 slide-in-from-top-2 duration-500'>
							<h1 className='text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text'>
								{brand.copy.configPageTitle}
							</h1>
							<p className='text-lg lg:text-xl text-gray-300'>
								Скопируйте ссылку-подписку и добавьте её в приложение — или
								откройте приложение одним нажатием
							</p>
						</div>

						<Card
							className='bg-amber-500/10 border-amber-500/40 backdrop-blur-sm animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both'
							style={{ animationDelay: '100ms' }}
						>
							<CardHeader>
								<CardTitle className='flex items-start gap-3 text-sm lg:text-base font-normal leading-relaxed text-amber-200'>
									<AlertTriangle className='w-5 lg:w-6 h-5 lg:h-6 text-amber-400 shrink-0 mt-0.5' />
									<span>{brand.copy.configBlockWarning}</span>
								</CardTitle>
							</CardHeader>
						</Card>

						<Card
							onClick={() =>
								window.open(brand.social.telegramSupportBot, '_blank', 'noopener,noreferrer')
							}
							className='cursor-pointer bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-300 animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both'
							style={{ animationDelay: '175ms' }}
						>
							<CardHeader>
								<CardTitle className='flex items-center gap-3 text-xl lg:text-2xl text-gray-100'>
									<ShieldQuestion className='w-6 lg:w-7 h-6 lg:h-7 text-blue-400' />
									Открыть чат со специалистом по подключению
								</CardTitle>
							</CardHeader>
						</Card>

						<Link
							href={brand.assets.instructionsDocUrl}
							target='_blank'
							className='block animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both'
							style={{ animationDelay: '250ms' }}
						>
							<Card className='bg-gradient-to-r from-gray-800/50 via-blue-800/50 to-purple-800/50 border-gray-700 backdrop-blur-sm bg-gray-800/70 mt-4 transition-all duration-300'>
								<CardHeader>
									<CardTitle className='flex items-center gap-3 text-xl lg:text-2xl text-gray-100'>
										<FileQuestion className='w-6 lg:w-7 h-6 lg:h-7 text-blue-400' />
										Открыть инструкцию по подключению
									</CardTitle>
								</CardHeader>
							</Card>
						</Link>

						{/* ===== Ваша подписка ===== */}
						<Card
							className='bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-300 animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both'
							style={{ animationDelay: '325ms' }}
						>
							<CardHeader>
								<CardTitle className='flex items-center gap-3 text-xl lg:text-2xl text-gray-100'>
									<Link2 className='w-6 lg:w-7 h-6 lg:h-7 text-green-400' />
									Ваша подписка
								</CardTitle>
								<CardDescription className='text-sm lg:text-base text-gray-400'>
									Одна ссылка со всеми доступными вам локациями — вставьте её в
									приложение
								</CardDescription>
							</CardHeader>
							<CardContent className='space-y-4 lg:space-y-6'>
								<div>
									<div className='flex items-center justify-between mb-2'>
										<p className='text-sm lg:text-base text-gray-400'>
											Ссылка-подписка
										</p>
										<Button
											variant='ghost'
											size='sm'
											className='h-8 text-gray-400 hover:text-white'
											disabled={!link?.url}
											onClick={copyLink}
										>
											<Copy className='w-4 h-4 mr-2' />
											Копировать
										</Button>
									</div>
									<div className='bg-gray-900 p-3 lg:p-4 rounded-lg'>
										<div className='max-h-[100px] overflow-y-auto'>
											<code className='text-sm lg:text-base text-green-400 break-all'>
												{linkLoading
													? 'Загрузка…'
													: link?.url || 'Не удалось получить ссылку'}
											</code>
										</div>
									</div>
								</div>

								{link?.url && (
									<div className='flex flex-col items-center gap-3'>
										<div className='flex items-center gap-2 text-sm lg:text-base text-gray-400'>
											<QrCode className='w-4 h-4' />
											Отсканируйте QR-код из приложения
										</div>
										<div className='bg-white p-3 rounded-lg'>
											<QRCode
												value={link.url}
												size={180}
												className='h-auto w-[180px]'
											/>
										</div>
									</div>
								)}
							</CardContent>
						</Card>

						{/* ===== Подключить приложение ===== */}
						<Card
							className='bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-300 animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both'
							style={{ animationDelay: '400ms' }}
						>
							<CardHeader>
								<CardTitle className='flex items-center gap-3 text-xl lg:text-2xl text-gray-100'>
									<Smartphone className='w-6 lg:w-7 h-6 lg:h-7 text-blue-400' />
									Подключить приложение
								</CardTitle>
								<CardDescription className='text-sm lg:text-base text-gray-400'>
									Выберите вашу систему, установите приложение и нажмите «Открыть
									в приложении»
								</CardDescription>
							</CardHeader>
							<CardContent className='space-y-4 lg:space-y-6'>
								<div className='flex flex-wrap gap-2'>
									{OS_OPTIONS.map(opt => (
										<Button
											key={opt.key}
											size='sm'
											onClick={() => setOs(opt.key)}
											className={
												os === opt.key
													? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
													: 'bg-gray-900/60 border border-gray-700 text-gray-300 hover:bg-gray-900'
											}
										>
											{opt.label}
										</Button>
									))}
								</div>

								{os === 'ios' && (
									<Link
										href={brand.assets.regionChangeInstructionsDocUrl}
										target='_blank'
										rel='noopener noreferrer'
										className='block'
									>
										<div className='flex items-center gap-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 lg:p-5 hover:bg-blue-500/20 transition-all duration-300'>
											<Globe className='w-7 h-7 lg:w-8 lg:h-8 text-blue-400 shrink-0' />
											<div className='min-w-0'>
												<p className='text-base lg:text-lg font-medium text-white'>
													Инструкция по смене региона
												</p>
												<p className='text-sm lg:text-base text-gray-400'>
													Если App Store не даёт скачать приложение — смените
													регион Apple ID на Казахстан
												</p>
											</div>
										</div>
									</Link>
								)}

								{appsLoading ? (
									<p className='text-sm lg:text-base text-gray-400'>
										Загрузка приложений…
									</p>
								) : apps.length === 0 ? (
									<p className='text-sm lg:text-base text-gray-400'>
										Для этой системы приложения пока не добавлены
									</p>
								) : (
									<div className='space-y-3'>
										{apps.map(app => {
											// Текстовая инструкция под конкретное приложение (если есть)
											const instructionUrl = os
												? brand.assets.appInstructions[os]?.[app.app_key]
												: undefined
											return (
												<div
													key={app.app_key}
													className='flex flex-wrap items-center justify-between gap-3 bg-gray-900/60 border border-gray-700 rounded-lg p-3 lg:p-4'
												>
													<div className='flex items-center gap-3 min-w-0'>
														{app.icon_url ? (
															// eslint-disable-next-line @next/next/no-img-element
															<img
																src={app.icon_url}
																alt={app.title}
																className='w-8 h-8 rounded shrink-0'
															/>
														) : (
															<Smartphone className='w-8 h-8 text-blue-400 shrink-0' />
														)}
														<span className='text-base lg:text-lg font-medium text-white truncate'>
															{app.title}
														</span>
													</div>
													<div className='flex flex-nowrap items-center gap-2 w-full sm:w-auto sm:ml-auto sm:justify-end max-[420px]:flex-col max-[420px]:items-stretch'>
														{instructionUrl && (
															<Link
																href={instructionUrl}
																target='_blank'
																rel='noopener noreferrer'
																className='flex-1 sm:flex-none max-[420px]:flex-none'
															>
																<Button
																	size='sm'
																	variant='outline'
																	className='w-full sm:w-auto border-gray-600 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white'
																>
																	<FileQuestion className='w-4 h-4 mr-2' />
																	Инструкция
																</Button>
															</Link>
														)}
														{app.scheme_template ? (
															<Button
																size='sm'
																onClick={() => openInApp(app)}
																disabled={!link?.url}
																className='flex-1 sm:flex-none max-[420px]:flex-none bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white'
															>
																<ExternalLink className='w-4 h-4 mr-2' />
																Открыть в приложении
															</Button>
														) : (
															<span className='text-xs lg:text-sm text-gray-400 text-right'>
																Скопируйте ссылку или отсканируйте QR
															</span>
														)}
													</div>

													{!!app.download_links?.length && (
														<div className='w-full pt-3 mt-1 border-t border-gray-800'>
															<div className='flex flex-wrap items-center gap-2'>
																{app.download_links.map((dl, i) => (
																	<Link
																		key={i}
																		href={dl.url}
																		target='_blank'
																		rel='noopener noreferrer'
																	>
																		<Button
																			size='sm'
																			variant='outline'
																			className='border-gray-600 bg-transparent text-gray-300 hover:bg-gray-800 hover:text-white'
																		>
																			<Download className='w-4 h-4 mr-2' />
																			{dl.title}
																		</Button>
																	</Link>
																))}
															</div>
														</div>
													)}
												</div>
											)
										})}
									</div>
								)}

								<p className='text-xs lg:text-sm text-gray-500'>
									Приложение устанавливается из App Store / Google Play (или с
									сайта разработчика). После установки вернитесь сюда и нажмите
									«Открыть в приложении».
								</p>
							</CardContent>
						</Card>
					</div>
			</PageContainer>
		</div>
	)
}

/**
 * Скелетон страницы конфигурации — повторяет тот же адаптивный каркас
 * (те же отступы и максимальная ширина), чтобы при загрузке не было
 * пустого экрана и скачка вёрстки.
 */
function ConfigurationSkeleton() {
	return (
		<div className='min-h-screen bg-gray-950'>
			<PageContainer className='py-6 lg:py-10'>
					<div className='space-y-4 lg:space-y-6'>
						{/* Заголовок */}
						<div className='space-y-2'>
							<Skeleton className='h-9 lg:h-11 w-2/3 max-w-md bg-gray-800' />
							<Skeleton className='h-6 lg:h-7 w-full max-w-2xl bg-gray-800' />
						</div>

						{/* Карточка-предупреждение + две карточки-ссылки */}
						<Skeleton className='h-20 w-full bg-gray-800/60' />
						<Skeleton className='h-16 lg:h-20 w-full bg-gray-800/60' />
						<Skeleton className='h-16 lg:h-20 w-full bg-gray-800/60' />

						{/* Ваша подписка */}
						<div className='rounded-xl border border-gray-700 bg-gray-800/50 p-4 lg:p-6 space-y-4 lg:space-y-6'>
							<Skeleton className='h-7 w-40 lg:w-48 bg-gray-700/50' />
							<Skeleton className='h-16 w-full bg-gray-900' />
							<div className='flex justify-center'>
								<Skeleton className='h-[180px] w-[180px] bg-gray-700/50' />
							</div>
						</div>

						{/* Подключить приложение */}
						<div className='rounded-xl border border-gray-700 bg-gray-800/50 p-4 lg:p-6 space-y-4 lg:space-y-6'>
							<Skeleton className='h-7 w-48 lg:w-56 bg-gray-700/50' />
							<div className='flex flex-wrap gap-2'>
								{Array.from({ length: 6 }).map((_, i) => (
									<Skeleton key={i} className='h-9 w-20 bg-gray-900' />
								))}
							</div>
							<div className='space-y-3'>
								{Array.from({ length: 3 }).map((_, i) => (
									<Skeleton key={i} className='h-16 w-full bg-gray-900' />
								))}
							</div>
						</div>
					</div>
			</PageContainer>
		</div>
	)
}
